import {
  AllowedService,
  BaseCategory,
  BookingSlot,
  BookingStatus,
  CustomerType,
  ErrorCodeCategory,
  TechnicianKycStatus,
  TechnicianLevel,
  TechnicianPosition,
  TechnicianStatus,
  TechnicianType,
} from './type';

export interface Dimensions {
  header: { width: number; height: number };
  sidebar: { width: number; height: number };
}

export interface IUser {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  type: CustomerType;
  isActive: number;
  isOtpVerify: number;
  createdAt: string;
}

export interface IService {
  _id: string;
  name: string;
  isActive: number;
  createdAt: string;
  key: string;
  category: BaseCategory;
  description: string[];
  terms: string[];
  banner_images: BannerImage[];
  icon: string;
  updatedAt: string;
}

export interface IMaterial {
  _id: string;
  name: string;
  unit: string; // FT, KG, PCS etc
  rate: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface IManageTool {
  _id: string;
  name: string;
  description: string;
  image: string;
  code: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface IGST {
  _id: string;
  name: string;
  gst: number;
  cgst: number;
  sgst: number;
  isActive: boolean;
  effectiveFrom: string;
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface BannerImage {
  type: string;
  url: string;
}

export interface ITechnician {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  active: number;
  createdAt: string;
  updatedAt: string;
  kycStatus: TechnicianKycStatus;
  status: TechnicianStatus;
  level: TechnicianLevel;
  profilePhoto: string;
  kycDocs: TechnicianDoc[];
  secondaryContactNumber: string;
  position: TechnicianPosition;
  type: TechnicianType;
  joiningDate: string;
  dob: string;
  experience?: string;
  toolBagRequests: IToolBagRequest[] | null;
  professionalSkills: ProfessionalSkill[];
  isPaired: boolean;
  IToolRequest: IToolRequest[] | null;
}

export interface ISellOldAC {
  _id: string;
  enquiryId: string;
  subType: string;
  status: string;
  createdAt: string;
  updatedAt: string;

  schedule: {
    slot: string;
    date: string;
  };

  user: {
    id: string;
    name: string;
    phoneNumber: string;
    createdAt: string;
  };

  service: {
    id: string;
    name: string;
  };

  technicianName: string;
}

export interface ISellOldACDetail {
  _id: string;
  user_id: string;
  enquiryId: string;

  type: string;
  subType: string;
  status: string;
  cancelReason?: string;

  noOfAc: number;

  /** RESIDENTIAL | COMMERCIAL — from old AC enquiry bulk/detail fields */
  propertyType?: string;
  totalNoOfAC?: number;
  brand?: string;
  alternateNumber?: string;

  createdAt: string;
  updatedAt: string;

  assignedTo: string;

  details: {
    serviceDetails: any[];
    propertyType?: string;
  };

  addOns: any[];

  schedule: {
    slot: string;
    date: string;
  };

  addressDetails: {
    house: string;
    street: string;
    city: string;
    state: string;
    zipcode: string;
    saveAs: string;
    landmark: string;
  };

  user_info: {
    _id: string;
    name: string;
    phoneNumber: string;
    countryCode: string;
    email: string;
    profilePhoto: string;
    type?: string;
  };

  technician_info: {
    _id: string;
    name: string;
    profilePhoto: string;
    countryCode: string;
    phoneNumber: string;
    email: string;
  };

  oldAcDetails: {
    _id: string;
    brand: string;
    model?: string;
    acType: string;
    tonnage: string;
    age?: string;
    condition: string;
    technology: string;
    photos: string[];
    inspectionDetails?: { offeredAmount?: number; [key: string]: any };
  }[];

  copperPipingDetails: Record<string, any>;
  amcDetails: Record<string, any>;
  consultancyDetails: Record<string, any>;
}

export interface ProfessionalSkill {
  acType: string;
  service: boolean;
  repair: boolean;
  install: boolean;
}

export interface TechnicianDoc {
  type: string;
  url: string;
  comment: string;
}

export interface IZone {
  id: number | string;
  name: string;
  pincodes: number[] | string[];
  isDisabled: boolean;
}

export interface IUserAddress {
  _id: string;
  userId: string;
  house: string;
  street: string;
  city: string;
  state: string;
  saveAs: 'HOME' | 'FRIEND' | 'OTHER' | 'WORK';
  landmark: string;
  zipcode: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  item: string;
  _id: string;
  quantity: string | number;
  price: number | string;
}
export interface IServiceDetails {
  service_id: string;
  service_data: IService;
  services: any[];
  serviceType: string;
  quantity: string;
  acType: string;
  place: string;
  comment: string;
  _id: string;
  otherService: string;
}

interface IServiceItem {
  service_id: string;
  serviceType: string;
}

export interface IBooking {
  _id: string;
  slot: BookingSlot;
  date: string;
  bookingId: string;
  invoiceUrl: string;
  invoiceCreated?: boolean;
  invoiceType?: 'STANDARD' | 'PROMOTIONAL' | 'WARRANTY';
  invoiceEditedAt?: string;
  status: BookingStatus;
  order: string;
  orderItems: OrderItem[];
  user: IUser;
  user_id: string;
  service: IService;
  services: IServiceItem[];
  serviceDetails: IServiceDetails[];
  addressDetails: IUserAddress[];
  addressId: string;
  amount: any;
  email: string;
  technician: ITechnician;
  technicianName: ITechnician;
  createdAt: Date;
  updatedAt: Date;
  tax: {
    gst: number;
    totalTax?: number;
  };
  applyGst?: boolean;
  grandTotal: number;
  originalTotal?: number;
  isCouponApplied?: boolean;
  discountAmount?: number;
  discountedTotal?: number;
  appliedCoupon?: {
    userCouponId?: string;
    couponCode?: string;
    discount?: number;
    minValue?: number;
    requestedAmount?: number;
  };
  cancelReason?: string;
  invoiceId?: string;
  userName?: string;
  serviceName?: string;
}
// Define ICreateServiceDetails using Omit to exclude unwanted fields
export type ICreateServiceDetails = Omit<
  IServiceDetails,
  'service_data' | '_id'
>;

// Define ICreateBooking using Pick and override serviceDetails with ICreateServiceDetails
export type ICreateBooking = Pick<
  IBooking,
  'user_id' | 'addressId' | 'slot' | 'date' | 'amount'
> & {
  serviceDetails: ICreateServiceDetails[];
  name?: string;
};

export enum ServiceType {
  BASIC = 'BASIC',
  GAS_CHG = 'GAS_CHG',
  COPPER_PIPING = 'COPPER_PIPING',
  AMC = 'AMC',
  COMM_AC = 'COMM_AC',
  OTHER = 'OTHER',
}

export const OptionsServiceType = [
  ServiceType.BASIC,
  ServiceType.GAS_CHG,
  ServiceType.COPPER_PIPING,
  ServiceType.COMM_AC,
  ServiceType.AMC,
  ServiceType.OTHER,
];
export const EnumOptions: Record<any, string> = {
  [ServiceType.BASIC]: 'Basic',
  [ServiceType.GAS_CHG]: 'Gas Charge',
  [ServiceType.COPPER_PIPING]: 'Copper Piping',
  [ServiceType.COMM_AC]: 'Commercial Air Conditioning',
  [ServiceType.AMC]: 'Annual Maintenance Contract',
  [ServiceType.OTHER]: 'Other',
};

export interface INewBooking {
  user_id: string;
  service_id: string;
  serviceDetails: {
    services: string[];
    serviceType: string;
    quantity: number | string;
    acType: string;
    place: string;
    comment: string;
    service_id: string;
  }[];
  addressId: string;
  slot: BookingSlot;
  date: string;
  amount: number;
  order_id: string;
}

export interface ICoupon {
  _id: string; // Unique identifier for the coupon
  couponCode: string; // The code of the coupon
  image?: string; // Optional image URL for the coupon
  name: string; // Name of the coupon
  discount: number; // Discount amount provided by the coupon
  minValue: number; // Minimum order value required to use the coupon
  expiryDate: Date; // Expiration date of the coupon
  description?: string[]; // Optional description of the coupon
  isActive: boolean; // Status of the coupon (active/inactive)
  createdAt: Date; // Date when the coupon was created
  updatedAt: Date; // Date when the coupon was last updated
}

export interface IBanner {
  destination: 'AD' | 'COUPON';
  imageUrl: string;
  position: number;
  _id?: string;
  data: string;
}
// Array of allowed services, ensuring it aligns with the AllowedService type
export const allowedServices: AllowedService[] = [
  'SERVICE',
  'REPAIR',
  'INSTALLATION',
  'COMPRESSOR',
  'GAS_CHARGING',
  'COMMERCIAL_AC',
  'AMC',
  'COPPER_PIPING',
  'OTHER',
];

// Basic services set, filtered from allowedServices
export const basicServices = new Set<AllowedService>([
  'SERVICE',
  'REPAIR',
  'INSTALLATION',
  'COMPRESSOR',
  'GAS_CHARGING',
]);

export interface IProduct {
  _id: string;
  name: string;
  brand: string;
  acType?: string;
  tonnage?: string;
  model?: string;
  mrp?: number;
  customerPrice?: number;
  featured?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProductListResponse {
  success: boolean;
  data: IProduct[];
  pagination: {
    totalPages: number;
    totalItems?: number;
    currentPage?: number;
    limit?: number;
  };
}

export interface IAMCRequest {
  _id: string;
  createdAt: string; //"2024-12-05T07:07:40.032Z",
  updatedAt: string; //"2024-12-05T07:07:40.032Z",
  userDetails: IUser;
  place: string;
  quantity: string | number;
  comment: string;
  leadId: string; // "LEAD-0512-9"
}

export interface IFreeConsultancyRequest {
  _id: string;
  addressDetails: IUserAddress;
  slot: BookingSlot;
  date: string; // '2024-12-10T00:00:00.000Z';
  status: BookingStatus;
  createdAt: string; //'2024-12-06T17:34:14.029Z';
  updatedAt: string; // '2024-12-06T17:35:17.175Z';
  userDetails: IUser;
  alternatePhone: string;
  documentURL: string;
  brand: IBrand;
  brandData: IBrand;
  consultancyId: string;
  place: string;
}

export interface IBrand {
  _id: string;
  name: string;
  brandLogoUrl?: string;
  isActive: 0 | 1;
  createdAt: string; // '2024-12-06T18:20:43.921Z';
  updatedAt: string; // '2024-12-06T18:35:34.469Z';
  globalErrorCodes: ErrorCode[];
  registeredDate?: string;
  errorCodeCount?: number;
}

export interface ErrorCode {
  _id: string;
  code: string;
  description: string;
  acType: string;
  models: string;
  solution: string[];
  category: ErrorCodeCategory;
}

export interface INotification {
  _id: string;
  title: string;
  body: string;
  type: string;
  createdAt: string; //'2024-12-12T18:25:19.619Z';
  updatedAt: string; //'2024-12-12T18:25:19.619Z';
}

export interface ITool {
  _id: string;
  name: string;
  description: string;
  image: string;
  quantity?: number;
  toolId?: string;
  isActive: boolean;
  createdAt?: string; // '2024-12-12T18:25:19.619Z';
  updatedAt?: string; // '2024-12-12T18:25:19.619Z';
}

export interface IToolBag {
  name: string;
  tools: ITool[];
  description: string;
  image?: string;
  _id?: string;
  createdAt?: string; // '2024-12-12T18:25:19.619Z';
  updatedAt?: string; // '2024-12-12T18:25:19.619Z';
}

export interface IToolBagRequest {
  _id: string;
  technician: ITechnician; // Ref to Technician model
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'HALTED';
  adminNote?: string;
  requestedAt: string; // ISO date string
  actionedAt?: string; // ISO date string (optional)
  actionedBy?: string | any; // Ref to Admin model (optional)
  assignedToolBag?: IToolBag | null; // Ref to ToolBag (optional)
  isAssigned: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface IToolRequest {
  _id: string;
  name?: string;
  image?: string;
  total_qty?: number;
  assigned_tools?: Array<any>;
}

export interface ITechnicianAddress {
  _id?: string;
  technicianId?: string;
  house?: string;
  street?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  landmark?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ITechnicianDetailResponse {
  technician: ITechnician;
  address: ITechnicianAddress | null;
}

export interface IAMCRequest {
  _id: string;
  leadId: string;
  userDetails: IUser;
  place: string;
  quantity: string | number;
  comment: string;
  createdAt: string;
  updatedAt: string;
}

export interface ICopperPipingRequest {
  _id: string;
  enquiryId: string;
  subType: string;
  schedule: {
    slot: string;
    date: string;
  };
  status: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string;
    phoneNumber: string;
    createdAt: string;
  };
  service: {
    id: string;
    name: string;
  };
  technicianName: string;
}

